import capitalize from "lodash/capitalize";
import map from "lodash/map";
import { Link, useLocation } from "react-router-dom";
import { useState } from "react";

import Alert from "@mui/material/Alert";
import List from "@mui/material/List";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Collapse from "@mui/material/Collapse";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import { settingsState } from "@/state/settings";
import { useRecoilState } from "recoil";

import LogoutIcon from "@mui/icons-material/Logout";

interface PathItem {
  id: string;
  label: string;
  path?: string;
  submenu?: PathItem[];
}

interface Props {
  paths?: PathItem[];
  error?: string;
}

const SidePathList = ({ paths, error }: Props) => {
  const location = useLocation();
  const isMainPage = !location.pathname.startsWith("/dashboard");
  const initialExpanded: { [key: string]: boolean } = {};

  const [settings, _] = useRecoilState(settingsState);

  paths?.forEach((item) => {
    if (item.submenu?.some((subItem) => subItem.path === location.pathname)) {
      initialExpanded[item.id] = true;
    }
  });

  const [expanded, setExpanded] = useState<{ [key: string]: boolean }>(
    initialExpanded
  );

  const toggleExpand = (id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  if (error) {
    return <Alert severity="error">{(error as any).message}</Alert>;
  }

  const renderIcon = (id: string, isSelected: boolean) => {
    let iconName = "default";

    if (id.includes("list_account")) iconName = "sidebar_list_account_icon";
    if (id.includes("chat")) iconName = "sidebar_chats_icon";
    if (id.includes("logout")) {
      return <LogoutIcon sx={{ fontSize: 16, color: "error.main" }} />;
    }

    return (
      <img
        src={`/${iconName}.svg`}
        alt={`${iconName} icon`}
        width={16}
        height={16}
        style={{
          flexShrink: 0,
          filter: isSelected
            ? "invert(100%)" // turns black icons white
            : "invert(0%)",
        }}
      />
    );
  };

  return (
    // Sidebar Menu Height
    <Stack sx={{ height: "110px", mb: 4 }}>
      <List
        sx={{
          height: 0,
          "& ul": { padding: 0 },
        }}
        subheader={<li />}
      >
        {map(paths, (item) => {
          const isSelected = location.pathname === item.path;
          const isExpandable = !!item.submenu;

          const Wrapper = item.path ? Link : Stack;

          return (
            <Stack key={`item-${item.id}`}>
              <Stack
                component={Wrapper}
                id={`item-${item.id}`}
                onClick={() => {
                  if (isExpandable) toggleExpand(item.id);
                }}
                to={item.path || ""}
                sx={(theme) => ({
                  textDecoration: "none",
                  cursor: "pointer",
                  p: 1,
                  mb: 0.5,
                  gap: 0.5,
                  flexDirection: "row",
                  justifyContent: "space-between",
                  borderRadius: 1,
                  color: isSelected
                    ? theme.palette.mode === "dark"
                      ? "primary.main"
                      : "secondary.contrastText"
                    : "text.primary",
                  backgroundColor: isSelected
                    ? theme.palette.mode === "dark"
                      ? isMainPage
                        ? "background.default"
                        : "background.paper"
                      : "primary.dark"
                    : "transparent",
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? isMainPage
                          ? "background.default"
                          : "background.paper"
                        : isSelected
                        ? "primary.dark"
                        : "primary.light",
                    fontWeight: "bold",
                  },
                })}
              >
                <Stack
                  direction="row"
                  width="100%"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Stack
                    sx={{
                      alignItems: "center",
                      flexDirection: "row",
                      gap: 1,
                      overflow: "hidden",
                      justifyContent: settings.isSideBarOpen
                        ? "flex-start"
                        : "center",
                      width: "100%",
                    }}
                  >
                    {renderIcon(item.id, isSelected)}

                    {settings.isSideBarOpen && (
                      <Typography
                        sx={{
                          fontWeight: 500,
                          fontSize: "13px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          color: item.id.includes("logout")
                            ? "error.main"
                            : "inherit",
                        }}
                      >
                        {capitalize(item.label || "Unknown")}
                      </Typography>
                    )}
                  </Stack>
                  {isExpandable &&
                    (expanded[item.id] ? (
                      <ExpandLessIcon sx={{ fontSize: 16 }} />
                    ) : (
                      <ExpandMoreIcon sx={{ fontSize: 16 }} />
                    ))}
                </Stack>
              </Stack>

              {isExpandable && (
                <Collapse in={expanded[item.id]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.submenu?.map((subItem) => (
                      <Stack
                        key={`subitem-${subItem.id}`}
                        component={Link}
                        to={subItem.path || ""}
                        sx={{
                          textDecoration: "none",
                          px: 4,
                          py: 1,
                          fontSize: "13px",
                          fontWeight:
                            location.pathname === subItem.path
                              ? "bold"
                              : "default",
                          "&:hover": {
                            fontWeight: "bold",
                          },
                        }}
                      >
                        {subItem.label}
                      </Stack>
                    ))}
                  </List>
                </Collapse>
              )}
            </Stack>
          );
        })}
      </List>
    </Stack>
  );
};

export { SidePathList };
